<template>
  <div class="stats-container" :class="containerClass" :style="containerVars">
    <div
      v-for="(item, index) in items"
      :key="item.key || index"
      class="stats-card"
      :class="[cardClass, canClick(item) ? 'is-clickable' : '']"
      :style="[cardBg(item), cardSpan(item)]"
      @click="onCardClick(item, index)"
    >
      <!-- ICON -->
      <div
        class="stats-icon-wrap"
        :class="iconWrapClass"
        :style="{ backgroundColor: item.circleColor || 'rgba(255,255,255,0.8)' }"
      >
        <img class="stats-icon" :src="item.icon" :alt="item.text || item.title" />
      </div>

      <!-- CONTENT -->
      <div class="stats-content" :class="contentClass">
        <!-- summary -->
        <template v-if="type === 'summary'">
          <div class="stats-title">{{ item.title }}</div>
          <div class="stats-total">{{ item.total }}</div>
        </template>

        <!-- fixed -->
        <template v-else-if="type === 'fixed'">
          <div class="stats-total fixed-total">{{ item.total }}</div>
          <div class="stats-text fixed-text">{{ item.text }}</div>
        </template>

        <!-- stats -->
        <template v-else>
          <div class="stats-total">{{ item.total }}</div>
          <div class="stats-text">{{ item.text }}</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "StatsCards",
  props: {
    items: { type: Array, required: true },

    /**
     * type:
     * - "stats": dashboard nhiều ô
     * - "summary": 2 ô (maintenance)
     * - "fixed": dạng Figma fixing (2 ô, ô 2 rộng hơn)
     */
    type: { type: String, default: "stats" },

    // canh chữ cho type="stats"
    align: { type: String, default: "right" },

    // số cột cho type="stats"
    cols: { type: Number, default: 6 },

    // tổng cột nền cho type="fixed"
    fixedCols: { type: Number, default: 3 },

    /**
     * ✅ Nếu bạn không muốn set item.is_click từng item,
     * thì truyền clickableKeys = ['ready','fixing','accident']
     */
    clickableKeys: { type: Array, default: () => [] }
  },
  computed: {
    containerVars() {
      return {
        "--cols": this.cols,
        "--fixed-cols": this.fixedCols
      };
    },
    containerClass() {
      return {
        "is-stats": this.type === "stats",
        "is-summary": this.type === "summary",
        "is-fixed": this.type === "fixed"
      };
    },
    cardClass() {
      return {
        "card-stats": this.type === "stats",
        "card-summary": this.type === "summary",
        "card-fixed": this.type === "fixed"
      };
    },
    iconWrapClass() {
      return {
        "icon-stats": this.type === "stats",
        "icon-summary": this.type === "summary",
        "icon-fixed": this.type === "fixed"
      };
    },
    contentClass() {
      if (this.type === "summary") return "content-center";
      if (this.type === "fixed") return "content-fixed";

      if (this.align === "left") return "content-left";
      if (this.align === "center") return "content-center";
      return "content-right";
    }
  },
  methods: {
    cardBg(item) {
      return { backgroundColor: item.bgColor || "#f5f5f5" };
    },
    cardSpan(item) {
      if (this.type !== "fixed") return {};
      const span = item.span || 1;
      return { gridColumn: `span ${span}` };
    },

    /**
     * ✅ rule click:
     * 1) nếu item có is_click (true/false) => dùng cái này
     * 2) nếu không có is_click => dùng clickableKeys
     */
    canClick(item) {
      if (!item) return false;
      if (typeof item.is_click === "boolean") return item.is_click;
      const key = item.key;
      return !!key && this.clickableKeys.includes(key);
    },

    onCardClick(item, index) {
      if (!this.canClick(item)) return;
      this.$emit("click-item", {
        key: item?.key || index,
        item,
        index
      });
    }
  }
};
</script>

<style scoped>
/* ===== CONTAINER ===== */
.stats-container {
  display: grid;
  gap: 16px;
  width: 100%;
}

/* stats: theo cols */
.stats-container.is-stats {
  grid-template-columns: repeat(var(--cols, 6), 1fr);
}

/* summary: 2 cột */
.stats-container.is-summary {
  grid-template-columns: repeat(2, 1fr);
}

/* fixed: chia nền fixedCols (mặc định 3) để span */
.stats-container.is-fixed {
  grid-template-columns: repeat(var(--fixed-cols, 3), 1fr);
}

/* ===== CARD BASE ===== */
.stats-card {
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  padding: 0 16px;
  overflow: hidden;
}

/* ✅ clickable */
.stats-card.is-clickable {
  cursor: pointer;
  transition: transform 0.08s ease, box-shadow 0.08s ease;
}
.stats-card.is-clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
}

/* ===== HEIGHT THEO TYPE ===== */
.card-stats { height: 117px; }
.card-summary { height: 110px; }
.card-fixed { height: 110px; }

/* ===== ICON ===== */
.stats-icon-wrap {
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-stats { width: 84px; height: 84px; }
.icon-summary { width: 84px; height: 84px; }
.icon-fixed { width: 76px; height: 76px; opacity: 0.95; }

.stats-icon {
  width: 56px;
  height: 56px;
  object-fit: contain;
}

/* ===== CONTENT ===== */
.stats-content {
  margin-left: 14px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.content-right { align-items: flex-end; text-align: right; }
.content-left { align-items: flex-start; text-align: left; }
.content-center { align-items: center; text-align: center; }

.content-fixed {
  align-items: center;
  text-align: center;
  justify-content: center;
}

/* ===== TEXT ===== */
.stats-total {
  font-size: 46px;
  font-weight: 800;
  line-height: 1;
  color: #111;
}

.stats-text {
  margin-top: 6px;
  font-size: 16px;
  color: #444;
  line-height: 1.1;
}

.stats-title {
  font-size: 16px;
  font-weight: 600;
  color: #334155;
  line-height: 1.2;
  margin-bottom: 6px;
}

.fixed-total { font-size: 54px; letter-spacing: 0.5px; }
.fixed-text { font-size: 16px; font-weight: 600; color: #334155; }

/* ===== RESPONSIVE ===== */
@media (max-width: 1200px) {
  .stats-container.is-stats { grid-template-columns: repeat(3, 1fr); }
  .stats-container.is-summary { grid-template-columns: 1fr; }
  .stats-container.is-fixed { grid-template-columns: 1fr; }
  .card-fixed { height: 108px; }
}

@media (max-width: 768px) {
  .stats-container.is-stats { grid-template-columns: repeat(2, 1fr); }
}
</style>
